import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, X, CaretLeft, CaretRight, Crown, 
  House, SignOut, List, MagnifyingGlass, Cross
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideoLibraryPage = () => {
  const { user, logout, isPaidMember, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [videoData, setVideoData] = useState({ featured: [], categories: [], is_paid_member: false });
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`${API}/videos/by-category`, {
          headers: getAuthHeader()
        });
        const data = response.data;
        
        if (data.featured.length === 0 && data.categories.length > 0) {
          const firstCategory = data.categories[0];
          if (firstCategory.videos && firstCategory.videos.length > 0) {
            data.featured = [firstCategory.videos[0]];
          }
        }
        
        setVideoData(data);
      } catch (error) {
        toast.error('Failed to load videos');
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, [getAuthHeader]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const openVideo = (video) => {
    if (video.is_premium && !videoData.is_paid_member) {
      toast.error('This is premium content. Upgrade to Inner Circle to access.');
      return;
    }
    setSelectedVideo(video);
  };

  const closeVideo = () => {
    setSelectedVideo(null);
  };

  const filterVideos = (videos) => {
    if (!searchQuery.trim()) return videos;
    const query = searchQuery.toLowerCase();
    return videos.filter(v => 
      v.title.toLowerCase().includes(query) || 
      v.description?.toLowerCase().includes(query)
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-[#C4A661] border-t-transparent animate-spin" />
          <p className="text-white/50">Loading videos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-[#F4F0E6]">
      {/* Glass Header */}
      <header className="fixed top-0 left-0 right-0 z-50 nav-glass">
        <div className="flex items-center justify-between px-6 md:px-12 lg:px-24 py-4">
          <Link 
            to="/community" 
            className="flex items-center gap-2 text-white/60 hover:text-[#F4F0E6] transition-colors"
          >
            <CaretLeft size={24} weight="bold" />
            <span className="font-medium text-sm uppercase tracking-widest">Back</span>
          </Link>
          
          <span className="text-2xl font-bold tracking-tight text-[#C4A661]">POWR</span>
          
          {/* Search */}
          <div className="relative">
            {showSearch ? (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 220, opacity: 1 }}
                className="flex items-center gap-2 bg-[#121212] border border-white/10 px-4 py-2"
              >
                <MagnifyingGlass size={16} className="text-white/50" />
                <input
                  type="text"
                  placeholder="Search videos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-transparent text-sm text-[#F4F0E6] outline-none w-full placeholder-white/30"
                  autoFocus
                />
                <button onClick={() => { setShowSearch(false); setSearchQuery(''); }}>
                  <X size={14} className="text-white/50 hover:text-[#F4F0E6]" />
                </button>
              </motion.div>
            ) : (
              <button
                onClick={() => setShowSearch(true)}
                className="p-2 hover:bg-white/5 transition-colors"
                data-testid="search-btn"
              >
                <MagnifyingGlass size={20} className="text-white/60" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-16">
        {/* Scripture Banner */}
        <div className="bg-[#C4A661] py-3">
          <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
            <p className="text-[#0A0A0A] text-sm font-medium">
              <Cross size={14} className="inline-block mr-2 -mt-0.5" weight="bold" />
              "Study to show thyself approved unto God, a workman that needeth not to be ashamed." — 2 Timothy 2:15
            </p>
          </div>
        </div>

        {/* Hero Featured Section */}
        {videoData.featured.length > 0 && (
          <FeaturedHero 
            video={videoData.featured[0]} 
            onPlay={openVideo}
            isPaid={videoData.is_paid_member}
          />
        )}

        {/* Category Rows */}
        <div className="px-6 md:px-12 lg:px-24 pb-20 space-y-10 -mt-20 relative z-10">
          {/* Featured Row */}
          {videoData.featured.length > 1 && (
            <VideoRow 
              title="Featured" 
              videos={filterVideos(videoData.featured.slice(1))} 
              onVideoClick={openVideo}
              isPaid={videoData.is_paid_member}
            />
          )}
          
          {/* Category Rows */}
          {videoData.categories.map((category) => (
            <VideoRow
              key={category.id}
              title={category.name}
              videos={filterVideos(category.videos)}
              onVideoClick={openVideo}
              isPaid={videoData.is_paid_member}
            />
          ))}
          
          {/* Empty State */}
          {videoData.categories.length === 0 && videoData.featured.length === 0 && (
            <div className="flex flex-col items-center justify-center py-32 text-center">
              <Play size={64} className="text-[#C4A661]/30 mb-4" />
              <h2 className="text-2xl font-bold text-[#F4F0E6] mb-2">No Videos Yet</h2>
              <p className="text-white/50 max-w-md">
                The video library is being curated. Check back soon for powerful content to fuel your journey.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Video Modal */}
      <AnimatePresence>
        {selectedVideo && (
          <VideoModal video={selectedVideo} onClose={closeVideo} />
        )}
      </AnimatePresence>
    </div>
  );
};

// Featured Hero Component
const FeaturedHero = ({ video, onPlay, isPaid }) => {
  if (!video) return null;
  
  const canPlay = !video.is_premium || isPaid;
  
  return (
    <div className="relative h-[70vh] md:h-[85vh]">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ 
          backgroundImage: `url(${video.thumbnail_url})`,
        }}
      />
      
      {/* Gradients */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/90 via-transparent to-transparent" />
      
      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 lg:px-24 pb-32 md:pb-40">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl"
        >
          {video.is_premium && (
            <div className="flex items-center gap-2 mb-4">
              <Crown size={16} weight="fill" className="text-[#C4A661]" />
              <span className="text-overline">Inner Circle Exclusive</span>
            </div>
          )}
          
          <h1 className="text-3xl md:text-5xl font-bold tracking-tighter text-[#F4F0E6] mb-4 leading-tight">
            {video.title}
          </h1>
          
          <p className="text-white/60 text-sm md:text-base mb-6 line-clamp-3">
            {video.description || 'Watch this powerful video to level up your game.'}
          </p>
          
          <div className="flex items-center gap-4">
            <Button
              onClick={() => onPlay(video)}
              disabled={!canPlay}
              data-testid="play-featured-btn"
              className={`flex items-center gap-2 px-8 py-4 text-lg font-bold ${
                canPlay 
                  ? 'bg-[#F4F0E6] text-[#050505] hover:bg-white' 
                  : 'bg-white/20 text-white/50 cursor-not-allowed'
              }`}
            >
              <Play size={24} weight="fill" />
              {canPlay ? 'Play' : 'Upgrade to Watch'}
            </Button>
            
            <span className="text-white/40 text-sm uppercase tracking-widest">
              {video.category_display}
            </span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Video Row Component (Netflix-style horizontal scroll)
const VideoRow = ({ title, videos, onVideoClick, isPaid }) => {
  const rowRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  if (!videos || videos.length === 0) return null;

  const scroll = (direction) => {
    if (!rowRef.current) return;
    const scrollAmount = rowRef.current.clientWidth * 0.8;
    rowRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  const handleScroll = () => {
    if (!rowRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = rowRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  return (
    <div className="relative group">
      <h2 className="text-xl md:text-2xl font-bold text-[#F4F0E6] mb-5 px-0">{title}</h2>
      
      {/* Left Arrow */}
      {showLeftArrow && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-full bg-gradient-to-r from-[#050505] to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-start pl-2"
        >
          <CaretLeft size={32} className="text-[#F4F0E6]" />
        </button>
      )}
      
      {/* Video Cards */}
      <div
        ref={rowRef}
        onScroll={handleScroll}
        className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {videos.map((video, index) => (
          <VideoCard 
            key={video.id} 
            video={video} 
            onClick={() => onVideoClick(video)}
            canPlay={!video.is_premium || isPaid}
            index={index}
          />
        ))}
      </div>
      
      {/* Right Arrow */}
      {showRightArrow && videos.length > 4 && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-full bg-gradient-to-l from-[#050505] to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-end pr-2"
        >
          <CaretRight size={32} className="text-[#F4F0E6]" />
        </button>
      )}
    </div>
  );
};

// Video Card Component
const VideoCard = ({ video, onClick, canPlay, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="flex-shrink-0 w-[180px] md:w-[260px] cursor-pointer group/card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      data-testid={`video-card-${video.id}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden">
        <img
          src={video.thumbnail_url}
          alt={video.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover/card:scale-110"
          loading="lazy"
        />
        
        {/* Overlay */}
        <div className={`absolute inset-0 bg-black/50 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <div className="absolute inset-0 flex items-center justify-center">
            {canPlay ? (
              <div className="w-14 h-14 bg-[#F4F0E6] flex items-center justify-center">
                <Play size={28} weight="fill" className="text-[#050505] ml-1" />
              </div>
            ) : (
              <div className="w-14 h-14 bg-[#C4A661] flex items-center justify-center">
                <Crown size={28} weight="fill" className="text-[#050505]" />
              </div>
            )}
          </div>
        </div>
        
        {/* Premium Badge */}
        {video.is_premium && (
          <div className="absolute top-2 right-2 bg-[#C4A661] px-2 py-1 flex items-center gap-1">
            <Crown size={12} weight="fill" className="text-[#050505]" />
            <span className="text-[10px] font-bold text-[#050505] uppercase">Premium</span>
          </div>
        )}
      </div>
      
      {/* Title */}
      <div className="mt-3">
        <h3 className="text-sm font-medium text-[#F4F0E6] line-clamp-2 group-hover/card:text-[#C4A661] transition-colors">
          {video.title}
        </h3>
        <p className="text-xs text-white/40 mt-1 uppercase tracking-widest">{video.category_display}</p>
      </div>
    </motion.div>
  );
};

// Video Modal Component
const VideoModal = ({ video, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 p-2 text-white/60 hover:text-[#F4F0E6] transition-colors"
          data-testid="close-video-modal"
        >
          <X size={32} />
        </button>
        
        {/* Video Player */}
        <div className="relative aspect-video bg-black overflow-hidden">
          <iframe
            src={`https://www.youtube.com/embed/${video.youtube_id}?autoplay=1&rel=0`}
            title={video.title}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        
        {/* Video Info */}
        <div className="mt-5 px-1">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-[#F4F0E6]">{video.title}</h2>
              <p className="text-white/40 text-sm mt-1 uppercase tracking-widest">{video.category_display}</p>
            </div>
            {video.is_premium && (
              <div className="flex items-center gap-1 bg-[#C4A661] px-4 py-1">
                <Crown size={14} weight="fill" className="text-[#050505]" />
                <span className="text-xs font-bold text-[#050505]">PREMIUM</span>
              </div>
            )}
          </div>
          {video.description && (
            <p className="text-white/60 text-sm mt-5 max-w-3xl">{video.description}</p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default VideoLibraryPage;
